import { selectionBudget } from '../../../select';
import type { DeploymentItem, SelectorNoun, SelectorStatus } from './types';

export type DeploymentSelectorLabelInputs = {
  noun: SelectorNoun;
  selectedItems: DeploymentItem[];
  /** Rows in hand, which may be one page of `totalCount`. */
  itemCount: number;
  totalCount?: number;
  status: SelectorStatus;
  /** The caller filters server-side, so an empty list means "no matches", not "nothing". */
  isServerSearched: boolean;
  multiple: boolean;
  maxSelection?: number;
  searchPlaceholder?: string;
};

export type DeploymentSelectorLabels = {
  /** Names the selection, for the chip and for whatever clears it. */
  chip: string;
  /** What the field skin's box says, in the filter bar's vocabulary. */
  field: string;
  /** What the chip skin says with nothing held. */
  placeholder: string;
  /** Why the trigger is refusing, if it is. */
  refusalHint?: string;
  searchBox: string;
  /** Sentence case: the counter heading is the only place the noun starts a phrase. */
  nouns: string;
  availableCount: number;
  /** The multi-select budget, when there is a cap. */
  selectionTally: string | null;
};

/** Everything the wording turns on, resolved once so each label below reads as a rule. */
type Wording = {
  noun: SelectorNoun;
  status: SelectorStatus;
  selectedItems: DeploymentItem[];
  /** Nothing to pick — as opposed to no match for a search still in the caller's hands. */
  hasNothingToOffer: boolean;
  availableCount: number;
  multiple: boolean;
  maxSelection?: number;
};

/**
 * Naming one of several picks would misrepresent the scope, so a multi-selection counts
 * itself. A lone pick is named: "1 deployment" withholds the one fact the chip is there to
 * carry, and the reader would have to open the menu to learn what they picked.
 */
const chipLabel = ({ noun, selectedItems }: Wording): string =>
  selectedItems.length === 1 ? (selectedItems[0]?.label ?? '') : `${selectedItems.length} ${noun.plural}`;

/** The legend already names the noun, so the box only says what is in it. */
const fieldText = ({ noun, status, selectedItems, hasNothingToOffer }: Wording): string => {
  if (selectedItems.length === 1) return selectedItems[0]?.label ?? '';
  if (selectedItems.length > 1) return `${selectedItems.length} selected`;
  if (hasNothingToOffer) return `No ${noun.plural} available`;
  if (status === 'loading') return 'Loading…';
  return 'Select…';
};

const placeholder = ({ noun, status, hasNothingToOffer, availableCount }: Wording): string => {
  if (hasNothingToOffer) return `No ${noun.plural} available`;
  if (status === 'loading') return `Loading ${noun.plural}...`;
  return `Select ${noun.singular} - ${availableCount} avail.`;
};

/** Loading comes first: mid-fetch there is nothing *yet*, which is not nothing at all. */
const refusalHint = ({ noun, status, hasNothingToOffer }: Wording): string | undefined => {
  if (status === 'loading') return `Loading ${noun.plural}...`;
  if (status === 'error') return `An error occurred while loading ${noun.plural}`;
  if (hasNothingToOffer) return `No ${noun.plural} are available`;
  return undefined;
};

/**
 * The budget, when there is a cap. It takes the menu's second line over from the refine
 * hint — with a cap in play the budget matters more than how much of the fleet is on screen.
 */
const selectionTally = ({ selectedItems, multiple, maxSelection }: Wording): string | null => {
  if (!multiple || typeof maxSelection !== 'number') return null;
  return selectionBudget(selectedItems.length, maxSelection);
};

/**
 * Every string the picker shows, derived in one place: the two skins word the same states
 * differently, and reading them side by side is what keeps them consistent.
 */
export const deploymentSelectorLabels = ({
  noun,
  selectedItems,
  itemCount,
  totalCount,
  status,
  isServerSearched,
  multiple,
  maxSelection,
  searchPlaceholder,
}: DeploymentSelectorLabelInputs): DeploymentSelectorLabels => {
  const wording: Wording = {
    noun,
    status,
    selectedItems,
    hasNothingToOffer: (itemCount === 0 && !isServerSearched) || status === 'error',
    // `itemCount` is one server-ranked page, not the whole set, so counting rows would report
    // the page size as the fleet size. Floored at the rows shown so the trigger can never
    // claim fewer than the menu lists.
    availableCount: typeof totalCount === 'number' ? Math.max(totalCount, itemCount) : itemCount,
    multiple,
    maxSelection,
  };

  return {
    chip: chipLabel(wording),
    field: fieldText(wording),
    placeholder: placeholder(wording),
    refusalHint: refusalHint(wording),
    searchBox: searchPlaceholder ?? `Search ${noun.singular}...`,
    nouns: `${noun.plural.charAt(0).toUpperCase()}${noun.plural.slice(1)}`,
    availableCount: wording.availableCount,
    selectionTally: selectionTally(wording),
  };
};
